import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate, inr } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/app/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Play, Check, Loader2, Circle, Search, Truck, HardHat, Briefcase, MessageSquare, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import Pagination from "@/components/app/Pagination";

const WORK_KEYS = ["Structure Installation", "Panel Installation", "Inverter Installation", "Electrical Works"];
const WORK_SHORT = { "Structure Installation": "Structural", "Panel Installation": "Panel", "Inverter Installation": "Inverter", "Electrical Works": "Electrical" };

function statusOf(item) { return item?.status || (item?.completed ? "completed" : "pending"); }
const isDone = (i) => statusOf(i) === "completed";
const isProg = (i) => statusOf(i) === "in_progress";

export default function Operations() {
  const [projects, setProjects] = useState([]);
  const [checklistsByProject, setChecklistsByProject] = useState({});
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});
  // Pagination — mirrors the /projects listing.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const load = () => {
    api.get("/projects?lite=1").then((r) => setProjects(r.data));
    api.get("/delivery-checklists").then((r) => {
      const grouped = {};
      for (const cl of r.data || []) {
        if (!cl.project_id) continue;
        (grouped[cl.project_id] = grouped[cl.project_id] || []).push(cl);
      }
      setChecklistsByProject(grouped);
    }).catch(() => setChecklistsByProject({}));
  };
  useEffect(() => { load(); }, []);

  const active = useMemo(() =>
    projects.filter((p) => (p.completion_pct ?? 0) < 100).filter((p) => {
      if (!q.trim()) return true;
      const s = q.toLowerCase();
      return [p.customer_name, p.location, p.qr_code, p.project_manager].some(
        (v) => v && String(v).toLowerCase().includes(s)
      );
    }), [projects, q]);

  const totalPages = Math.max(1, Math.ceil(active.length / pageSize));
  useEffect(() => { setPage(1); }, [q, pageSize]);
  const safePage = Math.min(page, totalPages);
  const paged = useMemo(
    () => active.slice((safePage - 1) * pageSize, safePage * pageSize),
    [active, safePage, pageSize]
  );

  const totals = useMemo(() => {
    let cls = 0, clsDone = 0, works = 0, worksDone = 0, stages = 0, stagesDone = 0;
    active.forEach((p) => {
      const clist = checklistsByProject[p.id] || [];
      cls += clist.length;
      clsDone += clist.filter((c) => c.status === "completed").length;
      (p.stages || []).filter((s) => WORK_KEYS.includes(s.key)).forEach((s) => { works++; if (isDone(s)) worksDone++; });
      (p.stages_v2 || []).forEach((s) => { stages++; if (s.status === "completed") stagesDone++; });
    });
    return { cls, clsDone, works, worksDone, stages, stagesDone };
  }, [active, checklistsByProject]);

  const updateStage = async (pid, key, patch) => {
    try { await api.post(`/projects/${pid}/stage`, { stage_key: key, ...patch }); load(); }
    catch { toast.error("Update failed"); }
  };

  return (
    <div data-testid="operations-page">
      <PageHeader
        eyebrow="Command Center"
        title="Project Operations"
        subtitle="Delivery checklists, field work and project stages on a single timeline. Add remarks, mark progress, see what unblocks what."
      />
      <div className="p-4 sm:p-8 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard testid="ops-active" label="Active Projects" value={active.length} />
          <StatCard testid="ops-materials" label="Checklists Delivered" value={`${totals.clsDone} / ${totals.cls}`} accent="warning" />
          <StatCard testid="ops-works" label="Site Works Done" value={`${totals.worksDone} / ${totals.works}`} accent="primary" />
          <StatCard testid="ops-stages" label="Milestones Complete" value={`${totals.stagesDone} / ${totals.stages}`} accent="success" />
        </div>

        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search project / customer / location…" className="rounded-none h-10 pl-9" data-testid="ops-search" />
        </div>

        <div className="space-y-4">
          {paged.map((p) => (
            <ProjectRow
              key={p.id}
              project={p}
              checklists={checklistsByProject[p.id] || []}
              expanded={!!expanded[p.id]}
              onToggle={() => setExpanded({ ...expanded, [p.id]: !expanded[p.id] })}
              onStage={(key, patch) => updateStage(p.id, key, patch)}
            />
          ))}
          {active.length === 0 && <div className="flat-card p-12 text-center text-sm text-[#9CA3AF]">No active projects.</div>}
          <Pagination page={safePage} totalPages={totalPages} pageSize={pageSize} total={active.length} onPageChange={setPage} onPageSizeChange={setPageSize} testidPrefix="ops-pagination" />
        </div>
      </div>
    </div>
  );
}

function ProjectRow({ project: p, checklists, expanded, onToggle, onStage }) {
  const works = (p.stages || []).filter((s) => WORK_KEYS.includes(s.key));
  const v2Stages = p.stages_v2 || [];
  const clDone = checklists.filter((c) => c.status === "completed").length;
  const worksDone = works.filter(isDone).length;
  const v2Done = v2Stages.filter((s) => s.status === "completed").length;

  return (
    <div className="flat-card" data-testid={`ops-project-${p.id}`}>
      <button
        onClick={onToggle}
        className="w-full px-5 py-4 border-b border-[#E5E7EB] flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-left hover:bg-[#F9FAFB]"
        data-testid={`ops-toggle-${p.id}`}
      >
        <div className="min-w-0 flex-1">
          <div className="label-eyebrow">{p.qr_code}</div>
          <div className="text-lg font-black tracking-tight">{p.customer_name}</div>
          <div className="text-xs text-[#4B5563]">{p.capacity_kw} kW · {p.location} · {inr(p.contract_value_inr)}</div>
        </div>
        <div className="flex items-center gap-4">
          <Pill icon={Truck} label="Checklists" done={clDone} total={checklists.length} color="#F59E0B" />
          <Pill icon={HardHat} label="Works" done={worksDone} total={works.length} color="#002FA7" />
          <Pill icon={Briefcase} label="Milestones" done={v2Done} total={v2Stages.length} color="#10B981" />
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-center gap-3">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#4B5563]">Completion</div>
        <div className="h-1.5 flex-1 bg-[#F3F4F6]">
          <div className="h-full bg-[#002FA7]" style={{ width: `${p.completion_pct}%` }} />
        </div>
        <div className="text-xs font-bold">{p.completion_pct}%</div>
        <Link to={`/projects/${p.id}`} className="text-[10px] font-bold text-[#002FA7] uppercase tracking-widest hover:underline">Open →</Link>
      </div>

      {expanded && (
        <div className="divide-y divide-[#F3F4F6]">
          <Section icon={Truck} title="Delivery Checklists" color="#F59E0B" items={checklists.length ? checklists.map(() => ({ status: "n/a" })) : null}>
            {checklists.length === 0 ? (
              <div className="flex items-center justify-between gap-3 py-2 flex-wrap">
                <span className="text-xs text-[#B45309] uppercase tracking-widest font-bold">
                  No delivery checklists yet — Material stage stays pending until one is created.
                </span>
                <Link
                  to={`/delivery-checklists?new_for_project=${p.id}`}
                  className="text-[10px] uppercase tracking-widest font-bold text-[#002FA7] hover:underline"
                  data-testid={`ops-create-checklist-${p.id}`}
                >
                  + Create Checklist
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-[#F3F4F6]">
                {checklists.map((c) => <ChecklistRow key={c.id} checklist={c} />)}
              </div>
            )}
          </Section>

          <Section icon={HardHat} title="Site Works" color="#002FA7" items={works.length ? works : null}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-[#E5E7EB]">
              {works.map((s) => (
                <ItemCell
                  key={s.key}
                  testKey={`work-${p.id}-${s.key.replace(/\s+/g, "-")}`}
                  label={WORK_SHORT[s.key] || s.key}
                  item={s}
                  onUpdate={(patch) => onStage(s.key, patch)}
                />
              ))}
            </div>
          </Section>

          <Section icon={Briefcase} title={`Project Tracker · ${v2Stages.length} Milestones`} color="#10B981" items={v2Stages.length ? v2Stages.map((s) => ({ status: s.status })) : null}>
            {v2Stages.length === 0 ? (
              <div className="text-xs text-[#9CA3AF] py-2">No V2 milestones yet — open the project to initialize.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 divide-y sm:divide-y-0">
                {v2Stages.map((s, idx) => (
                  <V2MilestoneTile
                    key={s.key}
                    index={idx}
                    projectId={p.id}
                    stage={s}
                    testKey={`v2stage-${p.id}-${s.key}`}
                  />
                ))}
              </div>
            )}
          </Section>
        </div>
      )}
    </div>
  );
}

const CL_STATUS_TONE = {
  draft: { bg: "#F3F4F6", fg: "#4B5563", label: "Draft" },
  submitted: { bg: "#DBEAFE", fg: "#1E40AF", label: "Submitted" },
  scheduled: { bg: "#DBEAFE", fg: "#1E40AF", label: "Scheduled" },
  in_progress: { bg: "#FEF3C7", fg: "#92400E", label: "In Progress" },
  partially_delivered: { bg: "#FEF3C7", fg: "#B45309", label: "Partially Delivered" },
  pending_items: { bg: "#FEE2E2", fg: "#991B1B", label: "Pending Items" },
  rescheduled: { bg: "#FED7AA", fg: "#9A3412", label: "Rescheduled" },
  completed: { bg: "#D1FAE5", fg: "#065F46", label: "Completed" },
  cancelled: { bg: "#E5E7EB", fg: "#4B5563", label: "Cancelled" },
};

function ChecklistRow({ checklist: c }) {
  const items = c.items || [];
  const totalReq = items.reduce((sum, it) => sum + Number(it.required_qty || 0), 0);
  const totalDel = items.reduce((sum, it) => sum + Number(it.delivered_qty || 0), 0);
  const pct = totalReq > 0 ? Math.round((totalDel / totalReq) * 100) : (c.status === "completed" ? 100 : 0);
  const tone = CL_STATUS_TONE[c.status] || CL_STATUS_TONE.draft;
  return (
    <Link
      to={`/delivery-checklists?open=${c.id}`}
      className="block px-3 py-2 hover:bg-[#F9FAFB]"
      data-testid={`ops-checklist-${c.id}`}
    >
      <div className="flex items-center gap-3 flex-wrap">
        <div className="font-mono text-xs font-bold text-[#0a0a0a] w-24 shrink-0">{c.checklist_no || "—"}</div>
        <span
          className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5"
          style={{ background: tone.bg, color: tone.fg }}
        >
          {tone.label}
        </span>
        {c.scheduled_delivery_date && (
          <span className="text-[10px] text-[#4B5563] whitespace-nowrap">
            Sched {fmtDate(c.scheduled_delivery_date)}
          </span>
        )}
        <div className="flex items-center gap-2 flex-1 min-w-[160px]">
          <div className="h-1.5 flex-1 bg-[#F3F4F6] min-w-16">
            <div className="h-full bg-[#002FA7]" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-[10px] font-mono text-[#4B5563] whitespace-nowrap">
            {totalDel}/{totalReq} · {pct}%
          </span>
        </div>
        <span className="text-[10px] uppercase tracking-widest font-bold text-[#002FA7] shrink-0">Open →</span>
      </div>
    </Link>
  );
}

function Pill({ icon: Icon, label, done, total, color }) {
  const complete = done === total && total > 0;
  return (
    <div className="hidden sm:flex flex-col items-end">
      <div className="flex items-center gap-1.5">
        <Icon className="w-3 h-3" style={{ color }} />
        <span className="text-[10px] uppercase tracking-widest font-bold text-[#4B5563]">{label}</span>
      </div>
      <div className="text-sm font-black tracking-tight" style={{ color: complete ? "#10B981" : "#0a0a0a" }}>
        {done} / {total}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, color, items, children }) {
  return (
    <div className="px-5 py-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color }} strokeWidth={2.5} />
        <div className="label-eyebrow" style={{ color }}>{title}</div>
        {items && (
          <div className="ml-auto text-[10px] uppercase tracking-widest font-bold text-[#4B5563]">
            {items.filter(isDone).length} of {items.length}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

function ItemCell({ testKey, label, item, onUpdate }) {
  const status = statusOf(item);
  const done = isDone(item), prog = isProg(item);
  const Icon = done ? Check : prog ? Loader2 : Circle;
  const color = done ? "#10B981" : prog ? "#002FA7" : "#9CA3AF";
  const bg = done ? "#D1FAE5" : prog ? "#DBEAFE" : "#F3F4F6";

  return (
    <div className="p-3 flex flex-col gap-2" data-testid={testKey}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center" style={{ background: bg }}>
            <Icon className={`w-3.5 h-3.5 ${prog ? "animate-spin" : ""}`} style={{ color }} strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-bold tracking-tight truncate">{label}</div>
            <span className="text-[9px] uppercase tracking-widest font-bold" style={{ color }}>{status.replace("_", " ")}</span>
          </div>
        </div>
        <RemarksButton item={item} onUpdate={onUpdate} testKey={`${testKey}-remarks`} />
      </div>
      <div className="flex gap-1">
        {status === "pending" && (
          <Button onClick={() => onUpdate({ status: "in_progress" })} size="sm" variant="outline" className="rounded-none border-[#002FA7] text-[#002FA7] text-[10px] uppercase tracking-widest font-bold h-7 flex-1" data-testid={`${testKey}-start`}>
            <Play className="w-3 h-3 mr-1" /> Start
          </Button>
        )}
        {!done && (
          <Button onClick={() => onUpdate({ status: "completed" })} size="sm" variant="outline" className="rounded-none border-[#10B981] text-[#10B981] text-[10px] uppercase tracking-widest font-bold h-7 flex-1" data-testid={`${testKey}-done`}>
            <Check className="w-3 h-3 mr-1" /> Done
          </Button>
        )}
        {done && (
          <Button onClick={() => onUpdate({ status: "pending" })} size="sm" variant="ghost" className="rounded-none text-[10px] uppercase tracking-widest font-bold h-7 flex-1 text-[#4B5563]" data-testid={`${testKey}-reopen`}>
            Reopen
          </Button>
        )}
      </div>
      {item.notes && (
        <div className="text-[10px] text-[#0a0a0a] bg-[#F9FAFB] border-l-2 border-[#002FA7] px-2 py-1 line-clamp-2">{item.notes}</div>
      )}
    </div>
  );
}

function CompactStage({ index, testKey, item, onUpdate }) {
  const status = statusOf(item);
  const done = isDone(item), prog = isProg(item);
  const Icon = done ? Check : prog ? Loader2 : Circle;
  const color = done ? "#10B981" : prog ? "#002FA7" : "#9CA3AF";
  const bg = done ? "#D1FAE5" : prog ? "#DBEAFE" : "#F3F4F6";

  const nextStatus = status === "pending" ? "in_progress" : status === "in_progress" ? "completed" : "pending";
  const nextLabel = status === "pending" ? "Start" : status === "in_progress" ? "Done" : "Reset";
  return (
    <div className="px-3 py-2 flex items-center gap-2 border-r border-b border-[#F3F4F6]" data-testid={testKey}>
      <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center" style={{ background: bg }}>
        <Icon className={`w-3 h-3 ${prog ? "animate-spin" : ""}`} style={{ color }} strokeWidth={2.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-bold tracking-tight truncate">{String(index + 1).padStart(2, "0")} · {item.key}</div>
        {item.notes && <div className="text-[9px] text-[#4B5563] truncate">{item.notes}</div>}
      </div>
      <button
        onClick={() => onUpdate({ status: nextStatus })}
        className="text-[9px] font-bold text-[#002FA7] uppercase tracking-widest hover:underline"
        data-testid={`${testKey}-action`}
      >
        {nextLabel}
      </button>
      <RemarksButton item={item} onUpdate={onUpdate} compact testKey={`${testKey}-remarks`} />
    </div>
  );
}

function V2MilestoneTile({ index, projectId, stage, testKey }) {
  const status = stage.status || "pending";
  const isDoneM = status === "completed";
  const isProgM = status === "in_progress";
  const Icon = isDoneM ? Check : isProgM ? Loader2 : Circle;
  const color = isDoneM ? "#10B981" : isProgM ? "#002FA7" : "#9CA3AF";
  const bg = isDoneM ? "#D1FAE5" : isProgM ? "#DBEAFE" : "#F3F4F6";
  const subs = stage.sub_activities || [];
  const subDone = subs.filter((sa) => sa.done).length;
  return (
    <Link
      to={`/projects/${projectId}`}
      className="px-3 py-2 flex items-center gap-2 border-r border-b border-[#F3F4F6] hover:bg-[#F9FAFB]"
      data-testid={testKey}
    >
      <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center" style={{ background: bg }}>
        <Icon className={`w-3 h-3 ${isProgM ? "animate-spin" : ""}`} style={{ color }} strokeWidth={2.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-bold tracking-tight truncate">
          {String(index + 1).padStart(2, "0")} · {stage.label}
        </div>
        <div className="text-[9px] text-[#4B5563]">{subDone}/{subs.length} activities · {status.replace("_", " ")}</div>
      </div>
    </Link>
  );
}


function RemarksButton({ item, onUpdate, compact, testKey }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(item.notes || "");
  const submit = () => { onUpdate({ notes: draft }); setOpen(false); toast.success("Remarks saved"); };
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setDraft(item.notes || ""); }}>
      <DialogTrigger asChild>
        <button
          className={`${item.notes ? "text-[#002FA7]" : "text-[#9CA3AF]"} hover:text-[#0a0a0a]`}
          title="Edit remarks"
          data-testid={testKey}
        >
          <MessageSquare className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} />
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-[#0a0a0a]">
        <DialogHeader><DialogTitle className="font-black text-xl tracking-tight">Remarks</DialogTitle></DialogHeader>
        <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="e.g., Vendor delayed shipment to 18th. Awaiting customer site clearance." rows={5} className="rounded-none" data-testid={`${testKey}-textarea`} />
        <Button onClick={submit} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-10 uppercase tracking-widest text-xs font-bold" data-testid={`${testKey}-save`}>Save</Button>
      </DialogContent>
    </Dialog>
  );
}
